/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-30
 */

// Add third-party dependencies.
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
} from '@mui/material';
import {
  School as GuideIcon,
  Business as StudioIcon,
  PersonAdd as InviteIcon,
  Search as BrowseIcon,
  Add as CreateIcon,
  Group as TeamIcon,
  CheckCircle as CompleteIcon,
  Email as EmailIcon,
  AccountCircle as ProfileIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Add local dependencies.
import Header from '../../layout/Header';
import { YouTubeVideoButton } from '../../common';

/**
 * Getting Started Guide component providing comprehensive onboarding information.
 * Covers studio workflows including joining, creating, and invitation processes.
 */
const GettingStarted: React.FC = () => {
  const navigate = useNavigate();

  const handleBrowseStudios = () => {
    navigate('/studios/add');
  };

  const handleCreateStudio = () => {
    navigate('/studios/create');
  };

  const handleContact = () => {
    navigate('/contact');
  };

  const handleDashboard = () => {
    navigate('/dashboard');
  };

  const handleProfile = () => {
    navigate('/profile');
  };

  const gettingStartedSteps = [
    {
      label: 'Verify Your Email Address',
      description:
        'Check your inbox and click the verification link to activate your account.',
      icon: <EmailIcon />,
    },
    {
      label: 'Complete Your Profile',
      description:
        'Add a professional photo, bio, job title, and connect your social media accounts.',
      icon: <ProfileIcon />,
    },
    {
      label: 'Choose Your Path',
      description:
        'Decide whether to join an existing studio or create your own.',
      icon: <StudioIcon />,
    },
    {
      label: 'Set Up Your Studio Experience',
      description:
        'Either browse and join studios, or create and customize your own.',
      icon: <CreateIcon />,
    },
    {
      label: 'Build Your Team',
      description:
        'Invite administrators and teachers to collaborate in your studio.',
      icon: <TeamIcon />,
    },
    {
      label: 'Start Managing',
      description:
        'Begin using tools.dance to streamline your studio operations.',
      icon: <CompleteIcon />,
    },
  ];

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      {/* Header */}
      <Header />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box textAlign="center" mb={6}>
          <GuideIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
              mb: 2,
            }}
          >
            Getting Started Guide
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              maxWidth: 700,
              mx: 'auto',
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              lineHeight: 1.5,
            }}
          >
            Welcome to tools.dance! This guide will help you understand how to
            join studios, create your own, and get the most out of our platform.
          </Typography>
        </Box>

        {/* Platform Overview */}
        <Card sx={{ mb: 6 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              About tools.dance
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}
            >
              <strong>tools.dance</strong> is a suite of web-based tools
              designed specifically for dance studio owners. Made by dance
              studio owners for dance studio owners, we understand the unique
              challenges you face every day.
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}
            >
              Our platform centers around <strong>studios</strong> –
              collaborative spaces that bring together owners, administrators,
              and teachers. Each studio is kept private and secure, allowing you
              to manage your organization effectively while maintaining complete
              control over your data.
            </Typography>
            <Box
              sx={{ bgcolor: 'primary.light', p: 3, borderRadius: 2, mt: 3 }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: 'primary.contrastText' }}
              >
                &ldquo;We Get It! We Got You! You&apos;re Good!&rdquo;
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Profile Completion Section */}
        <Card sx={{ mb: 6 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <ProfileIcon
                sx={{ fontSize: 40, color: 'primary.main', mr: 2 }}
              />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Complete Your Profile
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
              A complete profile helps you establish credibility with your team
              and makes collaboration more effective. Take a few minutes to
              personalize your account.
            </Typography>

            <Box sx={{ my: 3 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 2 }}
              >
                Profile Completion Checklist:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                  <strong>Upload a profile photo:</strong> Add a professional
                  headshot or photo to help your team recognize you
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                  <strong>Write a bio:</strong> Share your dance background,
                  expertise, and teaching philosophy
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                  <strong>Add your title:</strong> Let others know your role
                  (e.g., &ldquo;Dance Instructor&rdquo;, &ldquo;Studio
                  Owner&rdquo;, &ldquo;Choreographer&rdquo;)
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                  <strong>Include company information:</strong> Add your studio
                  or company name
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                  <strong>Connect social media:</strong> Link your website,
                  LinkedIn, Instagram, and Facebook profiles
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Configure notifications:</strong> Set your email, SMS,
                  and push notification preferences
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: 'action.hover',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Watch our step-by-step tutorial to learn how to update your
                profile with photos, bio, and more.
              </Typography>
              <YouTubeVideoButton
                videoId="QYDrwRvE2bU"
                videoTitle="How to Update Your Profile on tools.dance"
                buttonText="Watch Tutorial"
                variant="outlined"
                size="small"
              />
            </Box>

            <Button
              variant="contained"
              fullWidth
              onClick={handleProfile}
              sx={{ textTransform: 'none', py: 1.5 }}
            >
              Complete Your Profile
            </Button>
          </CardContent>
        </Card>

        {/* Studio Workflows */}
        <Typography
          variant="h3"
          component="h2"
          gutterBottom
          sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}
        >
          Understanding Studio Workflows
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 4,
            mb: 6,
          }}
        >
          {/* Joining Studios */}
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <BrowseIcon
                  sx={{ fontSize: 40, color: 'primary.main', mr: 2 }}
                />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Joining Existing Studios
                </Typography>
              </Box>
              <Typography variant="body1" paragraph>
                Browse available studios and request to join established dance
                communities:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Browse Studios:</strong> View public studios and their
                  information
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Request Access:</strong> Submit join requests to
                  studio owners
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Wait for Approval:</strong> Studio owners review and
                  approve requests
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Start Collaborating:</strong> Begin working with your
                  new team
                </Typography>
              </Box>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleBrowseStudios}
                sx={{ textTransform: 'none' }}
              >
                Browse Available Studios
              </Button>
            </CardContent>
          </Card>

          {/* Creating Studios */}
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CreateIcon
                  sx={{ fontSize: 40, color: 'primary.main', mr: 2 }}
                />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Creating Your Own Studio
                </Typography>
              </Box>
              <Typography variant="body1" paragraph>
                Set up your own studio and build your team from the ground up:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Studio Setup:</strong> Configure your studio name,
                  description, and settings
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Customize Branding:</strong> Add your studio&apos;s
                  unique identity
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Set Permissions:</strong> Define who can join and how
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Invite Your Team:</strong> Bring in administrators and
                  teachers
                </Typography>
              </Box>

              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: 'action.hover',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Watch our step-by-step tutorial to learn how to create your
                  own studio on tools.dance.
                </Typography>
                <YouTubeVideoButton
                  videoId="Ke3mmXrMpo0"
                  videoTitle="How to Create a New Studio on tools.dance"
                  buttonText="Watch Tutorial"
                  variant="outlined"
                  size="small"
                />
              </Box>

              <Button
                variant="contained"
                fullWidth
                onClick={handleCreateStudio}
                sx={{ textTransform: 'none' }}
              >
                Create New Studio
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Studio Invitations */}
        <Card sx={{ mb: 6 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <InviteIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Studio Invitations & Team Building
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
              Studio owners can invite team members in several ways, and
              understanding these processes helps everyone collaborate
              effectively:
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 3,
                mt: 3,
              }}
            >
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Direct Invitations
                </Typography>
                <Typography variant="body2">
                  Studio owners can send email invitations directly to specific
                  people, assigning roles like administrator or teacher upon
                  invitation.
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Public Join Requests
                </Typography>
                <Typography variant="body2">
                  Studios can allow public browsing where interested users can
                  submit join requests that owners review and approve.
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Role Management
                </Typography>
                <Typography variant="body2">
                  Owners can assign different permission levels to team members,
                  from basic teacher access to full administrative privileges.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Getting Started Steps */}
        <Typography
          variant="h3"
          component="h2"
          gutterBottom
          sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}
        >
          Your Getting Started Checklist
        </Typography>

        <Card sx={{ mb: 6 }}>
          <CardContent sx={{ p: 4 }}>
            <Stepper orientation="vertical">
              {gettingStartedSteps.map(step => (
                <Step key={step.label} active>
                  <StepLabel
                    icon={
                      <Box
                        sx={{
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {step.icon}
                      </Box>
                    }
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {step.description}
                    </Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card sx={{ mb: 6 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}
            >
              Ready to Get Started?
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 3,
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={handleBrowseStudios}
                sx={{ py: 2, textTransform: 'none' }}
              >
                Browse Studios
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleCreateStudio}
                sx={{ py: 2, textTransform: 'none' }}
              >
                Create Studio
              </Button>
              <Button
                variant="text"
                size="large"
                onClick={handleDashboard}
                sx={{ py: 2, textTransform: 'none' }}
              >
                Go to Dashboard
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 4 }} />

        {/* Additional Resources */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Need Additional Help?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Our team is here to support you every step of the way. We understand
            the challenges of running a dance studio because we live them too.
          </Typography>
          <Button
            variant="outlined"
            onClick={handleContact}
            sx={{ textTransform: 'none', px: 4, py: 1.5 }}
          >
            Contact Support
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default GettingStarted;
